<template>
  <CapacityDatabasePage
    :title="title"
    :hero="hero"
    :tabs="tabs"
    :is-authorized="isAuthorized"
  >
    <NuxtPage :methodology="methodologyFileInfo" :checkboxes="checkboxes" />
  </CapacityDatabasePage>
</template>

<script setup lang="ts">
import { useNuxtApp } from 'nuxt/app'
import CapacityDatabasePage from '~/components/capacity-database/CapacityDatabasePage.vue'
import { useAuthorization } from '~/composables/useAuthorization'
import { getServices } from '~/constants/services'
import {
  LITHIUM_ION_BATTERIES,
  MARKET_ASSESSMENTS,
} from '~/constants/services/constants'
import {
  CAPACITY_DATABASE_SUBSCRIPTIONS,
  METHODOLOGY_FILE_INFO,
} from '~/src/constants/capacity-database/common'
import type { TFileDownloadInfo } from '~/src/types/download'
import useAuthStore from '~/stores/auth'

/** app */
definePageMeta({
  layout: 'services',
})
const { $modal, $config } = useNuxtApp()
const authStore = useAuthStore()
const { hasMembership } = useAuthorization()

/** refs */
const market = LITHIUM_ION_BATTERIES

const checkboxes = [market.checkbox, MARKET_ASSESSMENTS.checkbox]

const METHODOLOGY_ENV_KEY = METHODOLOGY_FILE_INFO[market.alternativeSlug].EnvKey
const methodologyFileInfo: TFileDownloadInfo = {
  ...METHODOLOGY_FILE_INFO[market.alternativeSlug],
  ID: $config.public[METHODOLOGY_ENV_KEY],
}
const hero = {
  initialFields: checkboxes,
  title: market.capacityName,
  images: {
    desktop:
      'https://res.cloudinary.com/benchmarkminerals/image/upload/c_fill,q_auto,g_auto,w_1920,f_auto/v1719515546/assets/market/lithium-ion-batteries/data-and-reports/lithium-ion-batteries-data-and-reports_plstyc.png?_i=AA',
    mobile:
      'https://res.cloudinary.com/benchmarkminerals/image/upload/c_fill,q_auto,g_auto,w_1024,f_auto/v1719515546/assets/market/lithium-ion-batteries/data-and-reports/lithium-ion-batteries-data-and-reports_plstyc.png?_i=AA',
  },
  file: methodologyFileInfo,
}

const tabsBaseUrl = `/${market.alternativeSlug ?? market.slug}/capacity-database`

const isAuthorized = ref(false)

/** computed */
const isAuthenticated = computed(() => authStore.authenticated)

const title = computed(() => ({
  icon: market.iconColor,
  text: !isAuthenticated.value
    ? 'Gigafactory Assessment'
    : 'Battery & Gigafactory',
}))
const tabs = computed(() => {
  if (!isAuthenticated.value) return []
  return [
    {
      name: 'Lithium ion Capacities',
      url: `${tabsBaseUrl}/battery-cell`,
      slug: 'gigafactory',
    },
    {
      name: 'Sodium ion Capacities',
      url: `${tabsBaseUrl}/sodium-ion-capacities`,
      slug: 'tier-ranking',
    },
  ]
})

/** methods */
const getIsAuthorized = async () => {
  const subscription = CAPACITY_DATABASE_SUBSCRIPTIONS[market.slug]
  return hasMembership(subscription)
}

watch(isAuthenticated, async (isAuthenticated) => {
  if (!isAuthenticated) {
    isAuthorized.value = false
    return
  }
  isAuthorized.value = await getIsAuthorized()
})

onMounted(async () => {
  const initialValues = getServices(checkboxes)
  $modal.setInitialFields(initialValues)
})
</script>
